import React, { useState, Fragment, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Typography, 
  Row,
  Col,
  Input, 
  Select, 
  Button, 
  Drawer, 
  InputNumber,
  Switch, 
  Avatar, 
  Tag, 
  Pagination,
  Spin,
  Empty,
  Space,
  Modal,
  Table,
  Skeleton
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  EnvironmentOutlined, 
  GlobalOutlined,
  DollarOutlined,
  TeamOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { BootcampSearchResponse, BootcampApplicationResponse, AdvertisementType } from '../../types';
import { ApiDefinition } from '../../config';
import { filterQueryKeys, bootcampQueryKeys } from '../../lib/queryClient';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import FilterSortBar from '../../components/Common/FilterSortBar';
import AdvertisementDisplay from '../../components/Common/AdvertisementDisplay';
import OptimizedImage from '../../components/Common/OptimizedImage';
import dayjs from 'dayjs';
import styles from './Events.module.css';

const { Text } = Typography;
const { Option } = Select;

type BootcampSearchParams = {
  search: string;
  category: string;
  location: string;
  isOnline?: boolean;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortOrder?: string;
  page: number;
  pageSize: number;
};

const EVENTS_PAGE_SIZE = 5;

function parseBootcampParamsFromUrl(sp: URLSearchParams): BootcampSearchParams {
  return {
    search: sp.get('search') || '',
    category: sp.get('category') || '',
    location: sp.get('location') || '',
    isOnline: sp.get('isOnline') === 'true' ? true : undefined,
    minPrice: sp.get('minPrice') ? Number(sp.get('minPrice')) : undefined,
    maxPrice: sp.get('maxPrice') ? Number(sp.get('maxPrice')) : undefined,
    sortBy: sp.get('sortBy') || undefined,
    sortOrder: sp.get('sortOrder') || undefined,
    page: sp.get('page') ? Number(sp.get('page')) : 1,
    pageSize: sp.get('pageSize') ? Number(sp.get('pageSize')) : EVENTS_PAGE_SIZE
  };
}

const Events: React.FC = () => {
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isCompany = user?.role === 'Company';
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState<Pick<BootcampSearchParams, 'minPrice' | 'maxPrice' | 'isOnline'>>({
    minPrice: undefined,
    maxPrice: undefined,
    isOnline: undefined
  });
  const [participantsModalVisible, setParticipantsModalVisible] = useState(false);
  const [selectedBootcampId, setSelectedBootcampId] = useState<number | null>(null);
  const [participantsSearch, setParticipantsSearch] = useState('');
  const [participantsStatus, setParticipantsStatus] = useState<string | undefined>(undefined);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [participantsPageSize, setParticipantsPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState<BootcampSearchParams>(() =>
    parseBootcampParamsFromUrl(urlSearchParams)
  );

  useEffect(() => {
    const next = parseBootcampParamsFromUrl(urlSearchParams);
    setSearchParams(prev =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [urlSearchParams]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchParams.search) params.search = searchParams.search;
    if (searchParams.category) params.category = searchParams.category;
    if (searchParams.location) params.location = searchParams.location;
    if (searchParams.isOnline === true) params.isOnline = 'true';
    if (searchParams.minPrice != null) params.minPrice = String(searchParams.minPrice);
    if (searchParams.maxPrice != null) params.maxPrice = String(searchParams.maxPrice);
    if (searchParams.sortBy) params.sortBy = searchParams.sortBy;
    if (searchParams.sortOrder) params.sortOrder = searchParams.sortOrder;
    if (searchParams.page !== 1) params.page = String(searchParams.page);
    if (searchParams.pageSize !== EVENTS_PAGE_SIZE) params.pageSize = String(searchParams.pageSize);
    setUrlSearchParams(params, { replace: true });
  }, [searchParams]);

  const debouncedSearchParams = useDebouncedValue(searchParams, ApiDefinition.searchDebounceMs);
  const debouncedParticipantsSearch = useDebouncedValue(participantsSearch, ApiDefinition.searchDebounceMs);

  const { data: filters } = useQuery({
    queryKey: filterQueryKeys.bootcamp,
    queryFn: async () => {
      const response = await apiService.getBootcampFilters();
      if (!response.success) throw new Error(response.message);
      return response.data ?? {
        categories: [],
        locations: [],
        minPrice: 0,
        maxPrice: 1000
      };
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: bootcampResult, isLoading: bootcampLoading } = useQuery({
    queryKey: bootcampQueryKeys.search(debouncedSearchParams),
    queryFn: async () => {
      const response = await apiService.searchBootcamps(debouncedSearchParams);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: participantsResult, isLoading: participantsLoading } = useQuery({
    queryKey: bootcampQueryKeys.participants(selectedBootcampId!, {
      page: participantsPage,
      pageSize: participantsPageSize,
      search: debouncedParticipantsSearch || undefined,
      status: participantsStatus
    }),
    queryFn: async () => {
      const response = await apiService.getBootcampParticipants(
        selectedBootcampId!,
        participantsPage,
        participantsPageSize,
        debouncedParticipantsSearch || undefined,
        participantsStatus
      );
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: !!selectedBootcampId && participantsModalVisible,
    staleTime: 1000 * 60,
  });

  const bootcamps = bootcampResult?.data ?? [];
  const pagination = {
    current: bootcampResult?.pageNumber ?? 1,
    pageSize: bootcampResult?.pageSize ?? EVENTS_PAGE_SIZE,
    total: bootcampResult?.totalCount ?? 0
  };

  const participants = participantsResult?.data ?? [];
  const participantsPagination = {
    current: participantsResult?.pageNumber ?? 1,
    pageSize: participantsResult?.pageSize ?? 10,
    total: participantsResult?.totalCount ?? 0
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleDrawerOpen = () => {
    setDrawerFilters({
      minPrice: searchParams.minPrice,
      maxPrice: searchParams.maxPrice,
      isOnline: searchParams.isOnline
    });
    setFilterDrawerVisible(true);
  };

  const handleDrawerApply = () => {
    setSearchParams(prev => ({ ...prev, ...drawerFilters, page: 1 }));
    setFilterDrawerVisible(false);
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const handleResetFilters = () => {
    setSearchParams({
      search: '',
      category: '',
      location: '',
      isOnline: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      page: 1,
      pageSize: EVENTS_PAGE_SIZE
    });
  };

  // Aktif filtreleri chip olarak göster
  const activeFilters = useMemo(() => {
    const filters: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (searchParams.category) {
      filters.push({
        key: 'category',
        label: `Kategori: ${searchParams.category}`,
        onRemove: () => handleFilterChange('category', undefined)
      });
    }
    if (searchParams.location) {
      filters.push({
        key: 'location',
        label: `Lokasyon: ${searchParams.location}`,
        onRemove: () => handleFilterChange('location', undefined)
      });
    }
    if (searchParams.isOnline === true) {
      filters.push({
        key: 'isOnline',
        label: 'Online',
        onRemove: () => handleFilterChange('isOnline', undefined)
      });
    }
    if (searchParams.minPrice != null || searchParams.maxPrice != null) {
      filters.push({
        key: 'price',
        label: `Fiyat: ₺${searchParams.minPrice ?? 0} - ₺${searchParams.maxPrice ?? '∞'}`,
        onRemove: () => {
          setSearchParams(prev => ({ ...prev, minPrice: undefined, maxPrice: undefined, page: 1 }));
          setDrawerFilters(prev => ({ ...prev, minPrice: undefined, maxPrice: undefined }));
        }
      });
    }
    return filters;
  }, [searchParams]);

  const hasActiveFilters = activeFilters.length > 0 || searchParams.sortBy != null;

  const handleViewParticipants = (bootcampId: number) => {
    setSelectedBootcampId(bootcampId);
    setParticipantsModalVisible(true);
    setParticipantsPage(1);
    setParticipantsPageSize(10);
    setParticipantsSearch('');
    setParticipantsStatus(undefined);
  };

  const handleParticipantsSearch = () => {
    setParticipantsPage(1);
  };

  const handleParticipantsPageChange = (page: number, pageSize?: number) => {
    setParticipantsPage(page);
    if (pageSize) setParticipantsPageSize(pageSize);
  };

  const handleParticipantsStatusChange = (status: string | undefined) => {
    setParticipantsStatus(status);
    setParticipantsPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'green';
      case 'Rejected': return 'red';
      case 'Completed': return 'blue';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending': return 'Beklemede';
      case 'Approved': return 'Onaylandı';
      case 'Rejected': return 'Reddedildi';
      case 'Completed': return 'Tamamlandı';
      default: return status;
    }
  };

  const handleViewMenteeDetail = (menteeId: number) => {
    window.open(`/mentee/${menteeId}`, '_blank');
  };

  const participantsColumns = [
    {
      title: 'Profil',
      key: 'profile',
      width: 80,
      render: (_: any, record: BootcampApplicationResponse) => (
        <Avatar 
          size={40} 
          src={(record as any).mentee?.profileImage ? `data:image/jpeg;base64,${(record as any).mentee.profileImage}` : undefined}
          icon={<UserOutlined />}
        />
      ),
    },
    {
      title: 'Ad Soyad',
      key: 'name',
      render: (_: any, record: BootcampApplicationResponse) => (
        <Button
          type="link"
          onClick={() => handleViewMenteeDetail(record.menteeId)}
          style={{ padding: 0 }}
        >
          {(record as any).menteeName || `${(record as any).mentee?.firstName || ''} ${(record as any).mentee?.lastName || ''}`}
        </Button>
      ),
    },
    {
      title: 'E-posta',
      key: 'email',
      render: (_: any, record: BootcampApplicationResponse) => (
        <Text>{(record as any).menteeEmail || (record as any).mentee?.email || '-'}</Text>
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_: any, record: BootcampApplicationResponse) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusText(record.status)}
        </Tag>
      ),
    },
    {
      title: 'Başvuru Tarihi',
      key: 'appliedAt',
      render: (_: any, record: BootcampApplicationResponse) => (
        <Text>{dayjs(record.appliedAt).format('DD MMM YYYY HH:mm')}</Text>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: BootcampApplicationResponse) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewMenteeDetail(record.menteeId)}
        >
          Detay
        </Button>
      ),
    },
  ];

  const eventImageFallback = (
    <img
      src="/images/backgrounds/hero.png"
      alt="Etkinlik"
      style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }}
      role="presentation"
    />
  );

  const renderBootcampCardSkeleton = () => (
    <div className={styles.eventCardSkeleton}>
      <div className={styles.eventCardSkeletonImage} />
      <div className={styles.eventCardSkeletonBody}>
        <Skeleton active paragraph={false} title={{ width: '85%' }} />
        <Skeleton active paragraph={false} title={{ width: '50%' }} />
        <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
        <Skeleton.Button active size="small" style={{ width: 120, height: 32 }} />
      </div>
    </div>
  );

  const renderBootcampCard = (bootcamp: BootcampSearchResponse) => {
    const imageUrl = apiService.getBootcampImageUrl(bootcamp.id);
    const mentorImageUrl = apiService.getMentorProfileImageUrl(bootcamp.mentorId);
    return (
      <article key={bootcamp.id} className={styles.eventCard} onClick={() => navigate(`/bootcamp/${bootcamp.id}`)}>
        <div className={styles.eventCardImageWrap}>
          <OptimizedImage
            src={imageUrl}
            alt={bootcamp.title}
            width={280}
            height={180}
            objectFit="cover"
            loading="lazy"
            placeholder="blur"
            aria-label={`${bootcamp.title} bootcamp görseli`}
            fallback={eventImageFallback}
          />
        </div>
        <div className={styles.eventCardBody}>
          <div>
            <h2 className={styles.eventCardTitle}>{bootcamp.title}</h2>
            <div className={styles.eventCardMeta}>
              <Space size="small">
                <Avatar size="small" src={mentorImageUrl || undefined} icon={<UserOutlined />} onError={() => true} />
                <span>{bootcamp.mentorName}</span>
              </Space>
              <Tag icon={<CalendarOutlined />} color="blue">{dayjs(bootcamp.startDate).format('DD MMM YYYY')}</Tag>
              <Tag icon={bootcamp.isOnline ? <GlobalOutlined /> : <EnvironmentOutlined />} color="green">
                {bootcamp.isOnline ? 'Online' : bootcamp.location}
              </Tag>
              <span><TeamOutlined /> {bootcamp.currentParticipants}/{bootcamp.maxParticipants}</span>
            </div>
            <div className={styles.eventCardDesc}>
              {bootcamp.description?.replace(/<[^>]+>/g, '').slice(0, 140) || 'Etkinlik detayları için tıklayın.'}
              {bootcamp.description && bootcamp.description.length > 140 ? '…' : ''}
            </div>
          </div>
          <div className={styles.eventCardFooter}>
            <span className={styles.eventCardPrice}>₺{bootcamp.price.toLocaleString()}</span>
            <div className={styles.eventCardCta}>
              {isCompany && (
                <Button
                  size="small"
                  icon={<TeamOutlined />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleViewParticipants(bootcamp.id);
                  }}
                >
                  Katılımcılar
                </Button>
              )}
              <Button
                type="primary"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/bootcamp/${bootcamp.id}`);
                }}
              >
                Detay
              </Button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className={styles.eventsPage}>
      <div className={styles.container}>
        <div className={styles.hero}>
          <div className={styles.heroInner}>
            <h1 className={styles.heroTitle}>Etkinlikler</h1>
            <p className={styles.heroSubtitle}>Uzman eğitmenlerden öğrenin, kariyerinizi geliştirin</p>
          </div>
        </div>

        <div className={styles.adSlotBanner}>
          <AdvertisementDisplay pagePath="/events" type={AdvertisementType.Banner} position="top" />
        </div>

        <FilterSortBar
          searchPlaceholder="Etkinlik ara..."
          searchValue={searchParams.search}
          onSearchChange={(value) => setSearchParams(prev => ({ ...prev, search: value, page: 1 }))}
          onSearch={handleSearch}
          categoryOptions={filters?.categories || []}
          categoryValue={searchParams.category || undefined}
          onCategoryChange={(value) => handleFilterChange('category', value)}
          sortOptions={[
            { value: '', label: 'Varsayılan (En yeni)' },
            { value: 'createdAt_asc', label: 'En eski' },
            { value: 'title_asc', label: 'Başlık (A-Z)' },
            { value: 'title_desc', label: 'Başlık (Z-A)' },
            { value: 'startdate_asc', label: 'Başlangıç (yakından uzağa)' },
            { value: 'startdate_desc', label: 'Başlangıç (uzaktan yakına)' },
            { value: 'price_asc', label: 'Ücret (düşük → yüksek)' },
            { value: 'price_desc', label: 'Ücret (yüksek → düşük)' }
          ]}
          sortValue={searchParams.sortBy && searchParams.sortOrder ? `${searchParams.sortBy}_${searchParams.sortOrder}` : undefined}
          onSortChange={(value) => {
            if (!value) setSearchParams(prev => ({ ...prev, sortBy: undefined, sortOrder: undefined, page: 1 }));
            else {
              const [sortBy, sortOrder] = value.split('_');
              setSearchParams(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
            }
          }}
          showAdvancedFilters={true}
          onAdvancedFiltersOpen={handleDrawerOpen}
          advancedFilterCount={
            (searchParams.minPrice != null || searchParams.maxPrice != null ? 1 : 0) +
            (searchParams.isOnline === true ? 1 : 0)
          }
          onReset={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          activeFilters={activeFilters}
          mobileBreakpoint="md"
        />

        <Spin spinning={bootcampLoading}>
          {bootcampLoading && bootcamps.length === 0 ? (
            <div className={styles.eventList}>
              {Array.from({ length: EVENTS_PAGE_SIZE }).map((_, i) => (
                <Fragment key={`skeleton-${i}`}>{renderBootcampCardSkeleton()}</Fragment>
              ))}
            </div>
          ) : bootcamps && bootcamps.length > 0 ? (
            <>
              <div className={styles.eventList}>
                {bootcamps.map((bootcamp) => renderBootcampCard(bootcamp))}
              </div>
              <div className={styles.adSlotCard}>
                <AdvertisementDisplay pagePath="/events" type={AdvertisementType.Card} position="inline" />
              </div>
              <div className={styles.paginationWrap}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} etkinlik`}
                />
              </div>
            </>
          ) : (
            <div className={styles.emptyWrap}>
              <Empty description="Henüz etkinlik bulunmuyor" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
        </Spin>

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Gelişmiş Filtreler"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Fiyat Aralığı (₺)</Text>
            <Space.Compact style={{ width: '100%', gap: '8px', marginTop: '8px' }}>
              <InputNumber
                placeholder="Min"
                style={{ width: '100%' }}
                min={0}
                max={filters?.maxPrice ?? 10000}
                value={drawerFilters.minPrice ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, minPrice: v != null ? Number(v) : undefined }))}
                addonBefore="₺"
              />
              <InputNumber
                placeholder="Max"
                style={{ width: '100%' }}
                min={drawerFilters.minPrice ?? 0}
                max={(filters?.maxPrice ?? 10000) * 2}
                value={drawerFilters.maxPrice ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, maxPrice: v != null ? Number(v) : undefined }))}
                addonBefore="₺"
              />
            </Space.Compact>
          </div>

          <div>
            <Text strong>Etkinlik Türü</Text>
            <div style={{ marginTop: '8px' }}>
              <Switch
                checked={drawerFilters.isOnline === true}
                onChange={(checked) => setDrawerFilters(prev => ({ ...prev, isOnline: checked ? true : undefined }))}
              />
              <Text style={{ marginLeft: '8px' }}>Sadece Online</Text>
            </div>
          </div>

          <Button type="primary" onClick={handleDrawerApply} style={{ width: '100%' }}>
            Filtreleri Uygula
          </Button>
        </Space>
      </Drawer>

      {/* Participants Modal */}
      <Modal
        title="Katılımcılar"
        open={participantsModalVisible}
        onCancel={() => {
          setParticipantsModalVisible(false);
          setSelectedBootcampId(null);
          setParticipantsSearch('');
          setParticipantsStatus(undefined);
        }}
        width={1000}
        footer={null}
      >
        <div style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Input
                placeholder="Ad, soyad veya e-posta ile ara..."
                prefix={<SearchOutlined />}
                value={participantsSearch}
                onChange={(e) => {
                  setParticipantsSearch(e.target.value);
                  setParticipantsPage(1);
                }}
                onPressEnter={handleParticipantsSearch}
                allowClear
              />
            </Col>
            <Col span={8}>
              <Select
                placeholder="Durum filtresi"
                value={participantsStatus}
                onChange={handleParticipantsStatusChange}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value={undefined}>Tümü</Option>
                <Option value="Pending">Beklemede</Option>
                <Option value="Approved">Onaylandı</Option>
                <Option value="Rejected">Reddedildi</Option>
                <Option value="Completed">Tamamlandı</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleParticipantsSearch}>
                Ara
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          scroll={{ x: 'max-content' }}
          columns={participantsColumns}
          dataSource={participants}
          rowKey="id"
          loading={participantsLoading}
          pagination={false}
        />

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Pagination
            current={participantsPagination.current}
            pageSize={participantsPagination.pageSize}
            total={participantsPagination.total}
            onChange={handleParticipantsPageChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => 
              `${range[0]}-${range[1]} / ${total} katılımcı`
            }
          />
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default Events;


